/**
 * This module configures an axios instance with token refresh capabilities and case conversion middleware.
 * It handles authentication, token refresh, and concurrent request management using a mutex lock pattern.
 */

import type { TokenResponse } from "@/features/Auth/types/auth";
import {
  clearTokensFromStorage,
  setTokensInStorage,
} from "@/features/Auth/utils/tokenStorage";
import { isAuthenticatedAtom } from "@/store/authAtoms";
import { appStore } from "@/store/jotaiStore";
import { Mutex } from "async-mutex";
import axios, { type AxiosError, type InternalAxiosRequestConfig } from "axios";
import applyCaseMiddleware from "axios-case-converter";
import { removeEmptyKeys } from "./utils";

/**
 * Extended axios config type that includes the _retry flag.
 * This flag is used to prevent infinite refresh loops by tracking if a request
 * has already been retried after a token refresh.
 */
type ExtendedAxiosRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
};

/**
 * Storage keys for authentication tokens
 */
const KEYS = {
  ACCESS_TOKEN: "access_token",
  REFRESH_TOKEN: "refresh_token",
};

/**
 * Use a "Navigation handler" that allows for dependency injection for handling logout redirects.
 * This allows axios to remain decoupled from the router to avoid circular dependencies.
 */
type NavigationHandler = () => void;
let navigationHandler: NavigationHandler | null = null;
export const setNavigationHandler = (handler: NavigationHandler) => {
  navigationHandler = handler;
};

/**
 * Mutex lock for handling concurrent token refresh requests.
 * Ensures only one token refresh operation can occur at a time.
 */
const mutex = new Mutex();

/**
 * Subscribers for concurrent requests that are parked while a token refresh is
 * in flight. Each has a `resolve` (called with the new token on success) and a
 * `reject` (called with the refresh error on failure) so a parked request can
 * be woken up either way — never left hanging.
 */
type RefreshSubscriber = {
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
};
let refreshSubscribers: RefreshSubscriber[] = [];

/**
 * Adds a subscriber to be notified when the in-flight token refresh settles.
 * Used by concurrent requests that are waiting for a token refresh to complete.
 *
 * @param subscriber - resolve/reject pair for the waiting request
 */
const addRefreshSubscriber = (subscriber: RefreshSubscriber) => {
  refreshSubscribers.push(subscriber);
};

/**
 * Resolves all waiting requests with the new token and clears the subscriber
 * list. Called after a successful token refresh.
 *
 * @param token - The new access token to pass to subscribers
 */
const onRefreshed = (token: string) => {
  for (const { resolve } of refreshSubscribers) {
    resolve(token);
  }
  refreshSubscribers = [];
};

/**
 * Rejects all waiting requests and clears the subscriber list. Called when the
 * token refresh fails, so parked requests reject instead of hanging forever
 * (their promise would otherwise only ever be resolved).
 *
 * @param error - The refresh error to propagate to subscribers
 */
const onRefreshFailed = (error: unknown) => {
  for (const { reject } of refreshSubscribers) {
    reject(error);
  }
  refreshSubscribers = [];
};

/**
 * List of endpoints that should skip the token refresh process.
 * These are typically auth-related endpoints where refresh attempts would be circular.
 */
const SKIP_REFRESH_ENDPOINTS = [
  "/auth/refresh-token",
  "/auth/status",
  "/auth/token",
];

/**
 * Create axios instance with base configuration
 */
const axiosInstance = axios.create({
  baseURL: import.meta.env.GATEWAY_HOST,
});

/**
 * Handles user logout by clearing tokens and redirecting to login page.
 * Called when authentication fails or tokens cannot be refreshed.
 */
const handleLogout = () => {
  clearTokensFromStorage();
  appStore.set(isAuthenticatedAtom, false);
  if (navigationHandler) {
    navigationHandler();
  } else {
    // Fallback to window.location for cases where navigation handler isn't set
    window.location.href = "/login";
  }
};

/**
 * Attempts to refresh the access token using the refresh token.
 *
 * @throws Error if refresh token is not available or refresh fails
 * @returns Promise<string> - The new access token
 */
const refreshAccessToken = async (): Promise<string> => {
  try {
    const refreshToken = await localStorage.getItem(KEYS.REFRESH_TOKEN);
    if (!refreshToken) {
      throw new Error("No refresh token available");
    }

    const response = await axiosInstance.post<TokenResponse>(
      "/auth/refresh-token",
      {
        refresh_token: refreshToken,
      },
    );

    await setTokensInStorage(response.data);
    return response.data.accessToken;
  } catch (error) {
    handleLogout();
    throw error;
  }
};

/**
 * Waits for an ongoing token refresh to complete and retries the original request.
 * Used when multiple requests fail due to an expired token, ensuring only one
 * refresh request is made while others wait for the result.
 *
 * @param config - The original request configuration
 * @returns Promise - The retry request with the new token
 */
const waitForTokenRefreshAndRetry = async (
  config: ExtendedAxiosRequestConfig,
) => {
  const token = await new Promise<string>((resolve, reject) => {
    addRefreshSubscriber({ resolve, reject });
  });
  config.headers.Authorization = `Bearer ${token}`;
  return axiosInstance(config);
};

/**
 * Handles the token refresh process.
 * Acquires a mutex lock, refreshes the token, and notifies all waiting requests.
 *
 * @param config - The original request configuration
 * @returns Promise - The retry request with the new token
 */
const handleTokenRefresh = async (config: ExtendedAxiosRequestConfig) => {
  const release = await mutex.acquire();
  try {
    config._retry = true;
    const token = await refreshAccessToken();
    config.headers.Authorization = `Bearer ${token}`;
    onRefreshed(token);
    return axiosInstance(config);
  } catch (refreshError) {
    // Wake any parked concurrent requests so they reject instead of hanging.
    onRefreshFailed(refreshError);
    return Promise.reject(refreshError);
  } finally {
    release();
  }
};

/**
 * Handles 401 errors for endpoints that skip token refresh.
 * Logs the user out and rejects with the original error.
 *
 * @param error - The axios error object
 * @returns Promise.reject - Always rejects with the original error
 */
const handleSkippedEndpoint401 = (error: AxiosError) => {
  handleLogout();
  return Promise.reject(error);
};

/**
 * Request interceptor to add authentication and required headers.
 */
axiosInstance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const token = await localStorage.getItem(KEYS.ACCESS_TOKEN);

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Set default content type if not specified in the request
    if (!config.headers["Content-Type"]) {
      config.headers["Content-Type"] = "application/json";
    }

    // Needed to hit Gateway API
    config.headers["X-Client-Type"] = "professional_web";

    // Remove undefined GET query params to keep our API calls cleaner and more consistent
    if (config.method === "get") {
      config.params = removeEmptyKeys(config.params);
    }

    return config;
  },
);

/**
 * Response interceptor to handle token refresh and authentication errors.
 * Implements a mutex-based token refresh strategy to handle concurrent requests:
 * 1. First request to fail will refresh the token
 * 2. Subsequent requests will wait for the refresh to complete
 * 3. All requests retry with the new token
 */
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const is401Error = error.response?.status === 401;
    const shouldSkipRefresh = SKIP_REFRESH_ENDPOINTS.includes(
      originalRequest.url,
    );

    // Handle skipped endpoints or production environment
    if (shouldSkipRefresh && is401Error) {
      return handleSkippedEndpoint401(error);
    }

    // Handle token refresh
    if (is401Error && !originalRequest._retry) {
      if (mutex.isLocked()) {
        return waitForTokenRefreshAndRetry(
          originalRequest as ExtendedAxiosRequestConfig,
        );
      }
      return handleTokenRefresh(originalRequest as ExtendedAxiosRequestConfig);
    }

    // Handle failed refresh attempts
    if (is401Error && (originalRequest._retry || shouldSkipRefresh)) {
      return handleSkippedEndpoint401(error);
    }

    return Promise.reject(error);
  },
);

/**
 * Case conversion middleware options.
 * Preserves UUID keys while converting between camelCase and snake_case.
 */
const options = {
  preservedKeys: (input: string) => {
    // Check if the input is a valid UUID and if so, preserve that key
    // HACK: Also preserve "valueX" FHIR keys
    // HACK: Also preserve "_" prefixed keys, specifically important for translation keys
    //       https://linear.app/curai/issue/EHR-7271/translated-careplan-recommendations-break-timeline-view
    return (
      /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}/i.test(
        input,
      ) || input.startsWith("_")
    );
  },
};

export default applyCaseMiddleware(axiosInstance, options);
